/**
 * 가이드 검색 — 매뉴얼 제목·부제, 하고 싶은 일(목적 카드), 본문 제목(`##`·`###`)에서 찾는다.
 *
 * 🔑 띄어쓰기·대소문자를 보지 않는다 — 「타겟멘션추적」으로도 「타겟 멘션 추적」이 나온다.
 * 차례: 제목에 걸린 매뉴얼 → 부제에 걸린 매뉴얼 → 목적(짧은 이름·문장·설명)에 걸린 그 목적의 매뉴얼 → 본문 제목(목록 차례 그대로).
 * 목적으로 찾으면 「인플루언서 찾기」처럼 화면 이름에 없는 말로도 그 일을 돕는 매뉴얼이 나온다.
 */

import type { ManualEntry, ManualHeading, ManualPurpose, ManualHeadingsResponse } from 'src/types/manual/manual-domain';

import { paths } from 'src/routes/paths';

export type GuideSearchItem = {
  key: string;
  kind: 'manual' | 'heading';
  /** 결과 줄의 큰 글자 */
  label: string;
  /** 결과 줄의 작은 글자 — 매뉴얼이면 부제, 제목이면 그 제목이 든 매뉴얼 이름 */
  hint: string;
  href: string;
};

/** 한 번에 보여 줄 결과 수 */
export const GUIDE_SEARCH_LIMIT = 8;

/**
 * 찾을 거리 — 매뉴얼 줄(부제 = `hint`) · 목적마다 찾을 글(짧은 이름·문장·설명)과 그 목적의 매뉴얼 열쇠 · 본문 제목 줄
 */
export type GuideSearchIndex = {
  manuals: GuideSearchItem[];
  purposes: { text: string; keys: readonly string[] }[];
  sections: GuideSearchItem[];
};

/**
 * @param catalog 매뉴얼 목록
 * @param headings 매뉴얼마다 본문 제목(아직 안 받았으면 빈 배열)
 * @param purposes 목적 카드 — 비우면 목적으로는 찾지 않는다
 */
export function buildGuideSearchIndex(
  catalog: readonly ManualEntry[],
  headings: ManualHeadingsResponse['items'],
  purposes: readonly ManualPurpose[] = []
): GuideSearchIndex {
  const manuals = catalog.map((entry) => ({
    key: entry.id,
    kind: 'manual' as const,
    label: entry.title,
    hint: entry.subtitle,
    href: paths.guide.manual(entry.id),
  }));
  const titleOf = new Map(catalog.map((entry) => [entry.id as string, entry.title]));
  const sections = headings.flatMap(({ id, headings: list }) => {
    const manualTitle = titleOf.get(id);
    return manualTitle ? list.map((heading) => toSectionItem(id, manualTitle, heading)) : [];
  });
  const purposeIndex = purposes.map((purpose) => ({
    text: [purpose.shortTitle, purpose.title, purpose.description].join(' '),
    keys: purpose.manuals,
  }));
  return { manuals, purposes: purposeIndex, sections };
}

/**
 * @param query 찾는 말 — 비면 빈 결과
 * @returns 많아야 `limit` 줄
 */
export function searchGuide(index: GuideSearchIndex, query: string, limit: number = GUIDE_SEARCH_LIMIT): GuideSearchItem[] {
  const needle = normalize(query);
  if (!needle) return [];
  const byTitle = index.manuals.filter((item) => normalize(item.label).includes(needle));
  const bySubtitle = index.manuals.filter((item) => !byTitle.includes(item) && normalize(item.hint).includes(needle));
  const purposeKeys = new Set(
    index.purposes.filter((purpose) => normalize(purpose.text).includes(needle)).flatMap((purpose) => purpose.keys)
  );
  const byPurpose = index.manuals.filter(
    (item) => purposeKeys.has(item.key) && !byTitle.includes(item) && !bySubtitle.includes(item)
  );
  const bySection = index.sections.filter((item) => normalize(item.label).includes(needle));
  return [...byTitle, ...bySubtitle, ...byPurpose, ...bySection].slice(0, limit);
}

// ---- private ------------------------------------------------------------

function normalize(text: string): string {
  return text.toLowerCase().replace(/[\s·・\-—_]+/g, '');
}

function toSectionItem(id: string, manualTitle: string, heading: ManualHeading): GuideSearchItem {
  return {
    key: `${id}#${heading.id}`,
    kind: 'heading',
    label: heading.label,
    hint: manualTitle,
    href: `${paths.guide.manual(id)}#${encodeURIComponent(heading.id)}`,
  };
}
